<script setup lang="ts">
import DateField from '@/components/forms/DateField.vue';
import OrganizationEmployeeForm from '@/components/forms/organizationEmployee/UpsertOrganizationEmployeeForm.vue';
import { FeatureRole } from '@/enums/roles';
import { useDialogStore } from '@/stores/components/dialog';
import { useEmployeeStore } from '@/stores/employee';
import type { Employee, OrganizationEmployee, OrganizationEmployeeProperties } from '@/types/employee';
import { type EmployeeForm } from '@/types/employee';
import type { Organization } from '@/types/organization';
import type { UpsertOrganizationEmployeesRequest } from '@/types/requests/organizationEmployees/upsertOrganizationEmployeesRequest';
import { formatDateFieldDate, minimumDate } from '@/utils/date';
import { getEmployeeId } from '@/utils/employee';
import { processErrorWithFeedback } from '@/utils/error';
import toast from '@/utils/toast';
import { dateRange, email, maxLength, required } from '@/utils/validation';
import { onMounted, ref, useTemplateRef } from 'vue';

const dialogStore = useDialogStore();
const employeeStore = useEmployeeStore();

const props = defineProps<{
    organization?: Organization;
    organizationEmployee?: OrganizationEmployee;
}>();

const emit = defineEmits(['closeDialog', 'update-role', 'update-readonly']);

const organizationEmployeeForm = useTemplateRef('organizationEmployeeForm');

const isLoading = ref(false);
const form = useTemplateRef('form');
const dateOfBirthMax = formatDateFieldDate(new Date());

const employeeForm = ref<EmployeeForm>({} as EmployeeForm);
const isEdit = !!props.organizationEmployee;

onMounted(() => {
    if (isEdit) employeeForm.value = { ...(props.organizationEmployee?.employee as Employee) };
});

function closeDialog() {
    clearInput();
    emit('closeDialog');
}

function clearInput() {
    form.value?.reset();
}

/**
 * Upsert function for handling both Employee and the OrganizationEmployeeSettings
 */
async function upsert(): Promise<void> {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    isLoading.value = true;
    if (!props.organization) return;

    try {
        employeeForm.value.fullName = `${employeeForm.value.firstName} ${employeeForm.value.lastName}`;

        const upsertedEmployee = await handleEmployeeUpsert(employeeForm.value, props.organization);
        if (!upsertedEmployee) return;

        employeeForm.value._id = upsertedEmployee._id;
        await upsertOrganizationEmployee();
        updateEmployeeList(upsertedEmployee, organizationEmployeeForm.value!.request);
        closeDialog();
    } finally {
        isLoading.value = false;
    }
}

async function upsertOrganizationEmployee(): Promise<void> {
    try {
        if (!employeeForm.value._id) throw new Error();
        return await organizationEmployeeForm.value?.submit([employeeForm.value._id]);
    } catch (error) {
        processErrorWithFeedback('Medewerkersrechten konden niet worden ingesteld', error);
    }
}

async function handleEmployeeUpsert(employeeForm: EmployeeForm, organization: Organization): Promise<Employee | null> {
    const result = isEdit ? await employeeStore.updateEmployee(employeeForm) : await employeeStore.create(employeeForm, organization._id);
    return result.object;
}

function updateEmployeeList(upsertedEmployee: Employee, request: UpsertOrganizationEmployeesRequest) {
    if (!isEdit) {
        employeeStore.employeesInOrganizations.push({
            employee: upsertedEmployee,
            ...request
        } as OrganizationEmployee);
        return;
    }

    const index = employeeStore.employeesInOrganizations.findIndex((orgEmp) => getEmployeeId(orgEmp.employee) === upsertedEmployee._id.toString());
    if (index == -1) {
        toast.error('FailedToUpdateEmployeeList');
        return;
    }

    const updatedOrgEmp = { ...request, employee: upsertedEmployee } as OrganizationEmployeeProperties;

    const hasFeatureRole = Object.values(FeatureRole).some((role) => request.roles?.includes(role));

    // TODO: only remove specific features that have a feature role instead of emptying all features
    if (!hasFeatureRole) updatedOrgEmp.features = [];
    employeeStore.employeesInOrganizations[index] = updatedOrgEmp;
}

function openMoveEmployeeDialog() {
    closeDialog();
    dialogStore.isMoveEmployeeDialogOpen = true;
}

defineExpose({ isEdit, isLoading, upsert, closeDialog, openMoveEmployeeDialog });
</script>

<template v-slot:default>
    <v-form ref="form">
        <v-text-field v-model="employeeForm.initials" label="Voorletters" required :rules="[required]" />
        <v-text-field v-model="employeeForm.firstName" label="Voornaam" required :rules="[required]" />
        <v-text-field v-model="employeeForm.lastName" label="Achternaam" required :rules="[required]" />
        <v-text-field v-model="employeeForm.email" label="Email" required :rules="[required, email]" />
        <DateField
            v-model="employeeForm.dateOfBirth"
            label="Geboortedatum"
            :min="minimumDate"
            :max="dateOfBirthMax"
            :rules="[dateRange(minimumDate, dateOfBirthMax)]"
        />

        <v-text-field v-model="employeeForm.staffNumber" label="Personeelsnummer" />
        <v-text-field v-model="employeeForm.jobTitle" label="Functie" maxlength="50" :rules="[maxLength(50)]" />

        <OrganizationEmployeeForm
            v-if="props.organization"
            :organization-id="props.organization._id"
            :organization-employee="props.organizationEmployee"
            class="mt-2"
            ref="organizationEmployeeForm"
        />
    </v-form>
</template>
